// ChoosePlanScreen — mandatory plan selector shown ONCE after activation.
// User MUST pick one of:
//   ₹29/mo  · MDCF Monthly Plan
//   ₹349/yr · Yearly MDCF Premium
//   ₹999/yr · All Reports Premium
// Razorpay Checkout opens, on success the user can access reports.
//
// Lives at /choose-plan (route added in App.js).
import { refreshMyPlan } from "@/lib/planDisplay";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/lib/AuthContext";
import { CheckCircle2, Loader2, Zap, Star, Crown, Sparkles, Clock, Brain, Heart } from "lucide-react";
import { startRazorpayCheckout } from "@/lib/razorpay";
import CountdownPill from "@/components/poshan/CountdownPill";
import OfferCountdownBig from "@/components/poshan/OfferCountdownBig";
import GetNowButton from "@/components/poshan/GetNowButton";
import FomoCounter from "@/components/poshan/FomoCounter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_ICONS = {
  mdcf_monthly: { icon: Star, color: "text-blue-600", bg: "from-blue-50 to-blue-100", border: "border-blue-300" },
  mdcf_yearly: { icon: Crown, color: "text-purple-600", bg: "from-purple-50 to-purple-100", border: "border-purple-400", popular: true },
  all_reports_yearly: { icon: Sparkles, color: "text-amber-600", bg: "from-amber-50 to-orange-100", border: "border-amber-400" },
};

export default function ChoosePlanScreen() {
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null); // planKey while paying

  // iter122 — visitor may have picked a plan on the login landing page BEFORE
  // signing up. We persist that intent in sessionStorage; show a banner here
  // and visually highlight the same plan so they don't have to re-pick.
  const [intendedPlan, setIntendedPlan] = useState(() => {
    try { return sessionStorage.getItem("mdm.intendedPlan") || ""; } catch { return ""; }
  });

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${API}/subscriptions/plans`);
        setPlans(resp.data.subscription_plans || []);
      } catch (err) {
        console.error(err);
        toast.error("Could not load plans");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSelect = async (plan) => {
    setPaying(plan.key);
    try {
      await startRazorpayCheckout({
        planKey: plan.key,
        user,
        onSuccess: async () => {
          toast.success(`${plan.label} activated! Redirecting to your dashboard…`);
          refreshMyPlan();
          await refreshMe();
          setTimeout(() => navigate("/dashboard"), 800);
        },
        onError: (msg) => {
          toast.error(`Payment failed: ${msg}`);
        },
        onDismiss: () => {
          toast.info("Payment cancelled. Pick any plan to continue.");
        },
      });
    } catch {
      // already surfaced
    } finally {
      setPaying(null);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-sand-50 via-amber-50/30 to-sand-50 text-ink-900">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10 text-center">
          <p className="inline-block rounded-full bg-emerald-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">
            ✨ Account Activated
          </p>
          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-ink-900 sm:text-5xl">
            Choose your subscription plan
          </h1>
          <p className="mt-3 text-sm text-ink-700 sm:text-base">
            Pick a plan to unlock reports & start saving hours every month.
          </p>
          {intendedPlan && (
            <div
              className="mx-auto mt-5 inline-flex max-w-md items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-800 shadow-sm"
              data-testid="intended-plan-banner"
            >
              <Sparkles className="h-4 w-4" />
              <span>You picked the highlighted plan earlier — ready to subscribe?</span>
              <button
                type="button"
                onClick={() => {
                  try { sessionStorage.removeItem("mdm.intendedPlan"); sessionStorage.removeItem("mdm.intendedPlanLabel"); } catch (e) {}
                  setIntendedPlan("");
                }}
                className="ml-1 text-emerald-600 hover:text-emerald-900"
                title="Dismiss"
              >×</button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-clay" />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3" data-testid="plans-grid">
            {plans.map((plan) => {
              const meta = PLAN_ICONS[plan.key] || PLAN_ICONS.mdcf_monthly;
              const Icon = meta.icon;
              const isPopular = meta.popular;
              const hasOffer = (plan.original_price_rupees || 0) > (plan.amount_rupees || 0);
              const discountPct = hasOffer
                ? Math.round(100 - (plan.amount_rupees / plan.original_price_rupees) * 100)
                : 0;
              return (
                <div
                  key={plan.key}
                  className={`relative overflow-hidden rounded-2xl border-2 ${meta.border} bg-gradient-to-br ${meta.bg} p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl sm:p-7 ${isPopular ? "ring-2 ring-purple-400 ring-offset-2" : ""} ${intendedPlan === plan.key ? "ring-4 ring-emerald-400 ring-offset-2" : ""}`}
                  data-testid={`plan-card-${plan.key}`}
                >
                  {/* iter125 — limited-time offer ribbon (top-left) */}
                  {hasOffer && plan.offer_badge && (
                    <div
                      className="absolute -left-1 top-3 z-10 rounded-r-full bg-gradient-to-r from-red-600 to-rose-500 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-white shadow-lg ring-2 ring-red-300/50"
                      data-testid={`plan-offer-badge-${plan.key}`}
                    >
                      {plan.offer_badge}
                    </div>
                  )}
                  {intendedPlan === plan.key && (
                    <div className="absolute left-4 top-4 rounded-full bg-emerald-600 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white shadow" data-testid={`plan-intended-badge-${plan.key}`}>
                      ✨ Your pick
                    </div>
                  )}
                  {isPopular && (
                    <div className="absolute right-4 top-4 rounded-full bg-purple-600 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white shadow">
                      ⭐ Most Popular
                    </div>
                  )}
                  <Icon className={`${hasOffer ? "mt-7" : ""} h-10 w-10 ${meta.color}`} />
                  <h2 className="mt-4 font-heading text-xl font-bold tracking-tight text-ink-900">
                    {plan.label}
                  </h2>
                  <div className="mt-3 flex flex-wrap items-baseline gap-2">
                    <span className="font-mono text-4xl font-bold text-ink-900">
                      ₹{plan.amount_rupees}
                    </span>
                    {hasOffer && (
                      <span className="font-mono text-lg font-semibold text-rose-600 line-through decoration-rose-500 decoration-2" data-testid={`plan-strike-${plan.key}`}>
                        ₹{plan.original_price_rupees}
                      </span>
                    )}
                    <span className="text-sm font-medium text-ink-700">
                      / {plan.validity_days === 30 ? "month" : "year"}
                    </span>
                    {hasOffer && (
                      <span className="rounded-md bg-emerald-100 px-1.5 py-0.5 text-[0.65rem] font-bold text-emerald-800">
                        You save ₹{plan.original_price_rupees - plan.amount_rupees} ({discountPct}%)
                      </span>
                    )}
                  </div>
                  {hasOffer && (plan.offer_label || plan.offer_ends_at) && (
                    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`plan-offer-row-${plan.key}`}>
                      {plan.offer_label && (
                        <p
                          className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2.5 py-1 text-[0.7rem] font-semibold text-red-700 ring-1 ring-red-200"
                          data-testid={`plan-offer-label-${plan.key}`}
                        >
                          ⏰ {plan.offer_label}
                        </p>
                      )}
                      <CountdownPill endsAt={plan.offer_ends_at} testId={`plan-countdown-${plan.key}`} size="lg" />
                    </div>
                  )}
                  {plan.psychological_cta && (
                    <p className="mt-3 text-sm font-medium italic text-clay-dark">
                      "{plan.psychological_cta}"
                    </p>
                  )}

                  {/* iter120 — psychological grab: time saved + mind + health */}
                  <div className="mt-4 space-y-1.5 rounded-lg bg-white/70 p-3 text-xs">
                    {plan.time_saved_per_month && (
                      <p className="flex items-start gap-1.5 text-emerald-800" data-testid={`plan-time-${plan.key}`}>
                        <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span><b>Time saved:</b> {plan.time_saved_per_month}</span>
                      </p>
                    )}
                    {plan.mind_benefit && (
                      <p className="flex items-start gap-1.5 text-violet-800" data-testid={`plan-mind-${plan.key}`}>
                        <Brain className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span><b>Mind:</b> {plan.mind_benefit}</span>
                      </p>
                    )}
                    {plan.health_benefit && (
                      <p className="flex items-start gap-1.5 text-rose-800" data-testid={`plan-health-${plan.key}`}>
                        <Heart className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span><b>Health:</b> {plan.health_benefit}</span>
                      </p>
                    )}
                  </div>

                  <ul className="mt-5 space-y-2">
                    {plan.benefits.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-ink-800">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  {/* iter127 — big eye-catching countdown directly under benefits */}
                  <OfferCountdownBig endsAt={plan.offer_ends_at} testId={`plan-countdown-big-${plan.key}`} />
                  {/* iter127 — psychological "Get Now" CTA */}
                  <GetNowButton
                    onClick={() => handleSelect(plan)}
                    priceRupees={plan.amount_rupees}
                    loading={paying === plan.key}
                    disabled={paying !== null}
                    testId={`plan-cta-${plan.key}`}
                  />
                  {/* iter128 — FOMO social-proof + scarcity counter */}
                  <FomoCounter planKey={plan.key} testId={`plan-fomo-${plan.key}`} />
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10 text-center text-xs text-ink-700">
          🔒 Payments are secure & processed by Razorpay. We never store your card details.
        </div>
      </div>
    </main>
  );
}
